import { useState, useEffect, useMemo } from 'react';
import { X, User, Users, Monitor, Settings, Plus, Trash2, Mail, Search, Check, AlertTriangle, RefreshCw } from 'lucide-react';
import { UserGroup, GroupMember, SyncedUser } from '../../types';
import { getGroupMembers, getClientUsers, addUserToGroup, removeUserFromGroup, deleteGroup, deleteDistributionList } from '../../services/api';
import toast from 'react-hot-toast';

interface CachedGroupMember {
  id: string;
  displayName: string;
  userPrincipalName: string | null;
  type: string;
}

interface GroupMemberModalProps {
  group: UserGroup;
  clientId: string;
  cachedMembers?: CachedGroupMember[];
  cachedUsers?: SyncedUser[];
  onClose: () => void;
  onGroupDeleted?: () => void;
}

interface OperationProgress {
  total: number;
  completed: number;
  currentUser: string;
  results: { userId: string; userName: string; success: boolean; error?: string }[];
}

// Get icon for member type
function getMemberIcon(type: GroupMember['type']) {
  switch (type) {
    case 'user':
      return <User className="h-5 w-5 text-blue-500" />;
    case 'group':
      return <Users className="h-5 w-5 text-purple-500" />;
    case 'device':
      return <Monitor className="h-5 w-5 text-green-500" />;
    case 'servicePrincipal':
      return <Settings className="h-5 w-5 text-orange-500" />;
    default:
      return <User className="h-5 w-5 text-muted-foreground" />;
  }
}

// Get label for member type
function getMemberTypeLabel(type: GroupMember['type']) {
  switch (type) {
    case 'user': return 'User';
    case 'group': return 'Group';
    case 'device': return 'Device';
    case 'servicePrincipal': return 'Service Principal';
    default: return 'Other';
  }
}

interface TenantUser {
  id: string;
  displayName: string;
  userPrincipalName: string;
  mail: string | null;
}

// Convert cached group members to GroupMember format
function cachedToGroupMembers(cached: CachedGroupMember[]): GroupMember[] {
  return cached.map(m => ({
    id: m.id,
    displayName: m.displayName,
    email: m.userPrincipalName,
    type: (m.type === 'user' || m.type === 'group' || m.type === 'device' || m.type === 'servicePrincipal')
      ? m.type
      : 'other' as const,
  }));
}

// Convert cached SyncedUser[] to TenantUser format
function syncedUsersToTenantUsers(users: SyncedUser[]): TenantUser[] {
  return users.map(u => ({
    id: u.id,
    displayName: u.displayName,
    userPrincipalName: u.userPrincipalName,
    mail: u.mail,
  }));
}

export default function GroupMemberModal({ group, clientId, cachedMembers, cachedUsers, onClose, onGroupDeleted }: GroupMemberModalProps) {
  const [members, setMembers] = useState<GroupMember[]>(
    cachedMembers ? cachedToGroupMembers(cachedMembers) : []
  );
  const [loading, setLoading] = useState(!cachedMembers);
  const [error, setError] = useState<string | null>(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [allUsers, setAllUsers] = useState<TenantUser[]>(
    cachedUsers ? syncedUsersToTenantUsers(cachedUsers) : []
  );
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [addingUser, setAddingUser] = useState<string | null>(null);
  const [removingUser, setRemovingUser] = useState<string | null>(null);

  // Multi-select mode for distribution groups (slow Exchange operations)
  const isDistributionGroup = group.groupType === 'Distribution' ||
    (group.mailEnabled && !group.securityEnabled);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [bulkProgress, setBulkProgress] = useState<OperationProgress | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Delete group handler — uses Exchange runbook for distribution groups, Graph API for others
  async function handleDeleteGroup() {
    try {
      setDeleting(true);

      let result: { success: boolean; error?: string; message?: string };

      if (isDistributionGroup && group.mail) {
        // Distribution groups must be deleted via Exchange Online runbook
        result = await deleteDistributionList(clientId, group.mail);
      } else {
        result = await deleteGroup(clientId, group.id);
      }

      if (result.success) {
        toast.success(`Group "${group.displayName}" deleted successfully`);
        if (onGroupDeleted) {
          onGroupDeleted();
        } else {
          onClose();
        }
      } else {
        toast.error(result.error || 'Failed to delete group');
        setShowDeleteConfirm(false);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete group';
      toast.error(msg);
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  }

  // Fetch members
  async function fetchMembers() {
    try {
      setLoading(true);
      setError(null);
      const data = await getGroupMembers(clientId, group.id);
      setMembers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load members');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    // Skip live fetch if we already have cached members
    if (!cachedMembers) {
      fetchMembers();
    }
  }, [clientId, group.id]);

  // Fetch all users when Add User section is opened (skip if we have cached users)
  useEffect(() => {
    if (showAddUser && allUsers.length === 0) {
      fetchAllUsers();
    }
  }, [showAddUser]);

  async function fetchAllUsers() {
    try {
      setLoadingUsers(true);
      const response = await getClientUsers(clientId);
      setAllUsers(response.users.map(u => ({
        id: u.id,
        displayName: u.displayName,
        userPrincipalName: u.userPrincipalName,
        mail: u.mail
      })));
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  }

  // Filter users not already in the group
  const availableUsers = useMemo(() => {
    const memberIds = new Set(members.map(m => m.id));
    return allUsers.filter(u =>
      !memberIds.has(u.id) &&
      (userSearch === '' ||
        u.displayName.toLowerCase().includes(userSearch.toLowerCase()) ||
        u.userPrincipalName.toLowerCase().includes(userSearch.toLowerCase()))
    );
  }, [allUsers, members, userSearch]);

  const [permissionError, setPermissionError] = useState(false);

  // Add user to group
  async function handleAddUser(userId: string, userName: string) {
    try {
      setAddingUser(userId);
      const result = await addUserToGroup(clientId, userId, group.id);
      if (result.success) {
        toast.success(`Added ${userName} to group`);
        await fetchMembers();
        setUserSearch('');
      } else {
        if (result.error?.includes('Insufficient privileges') || result.error?.includes('Authorization')) {
          setPermissionError(true);
          toast.error('Missing GDAP permission: Groups Administrator');
        } else {
          toast.error(result.error || 'Failed to add user');
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to add user';
      if (msg.includes('Insufficient privileges') || msg.includes('Authorization')) {
        setPermissionError(true);
        toast.error('Missing GDAP permission: Groups Administrator');
      } else {
        toast.error(msg);
      }
    } finally {
      setAddingUser(null);
    }
  }

  // Remove user from group
  async function handleRemoveUser(member: GroupMember) {
    if (member.type !== 'user') {
      toast.error('Can only remove users from groups');
      return;
    }

    try {
      setRemovingUser(member.id);
      const result = await removeUserFromGroup(clientId, member.id, group.id);
      if (result.success) {
        toast.success(`Removed ${member.displayName} from group`);
        await fetchMembers();
      } else {
        if (result.error?.includes('Insufficient privileges') || result.error?.includes('Authorization')) {
          setPermissionError(true);
          toast.error('Missing GDAP permission: Groups Administrator');
        } else {
          toast.error(result.error || 'Failed to remove user');
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to remove user';
      if (msg.includes('Insufficient privileges') || msg.includes('Authorization')) {
        setPermissionError(true);
        toast.error('Missing GDAP permission: Groups Administrator');
      } else {
        toast.error(msg);
      }
    } finally {
      setRemovingUser(null);
    }
  }

  // Toggle user selection (for multi-select mode)
  function toggleUserSelection(userId: string) {
    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  }

  // Select/deselect all visible users
  function toggleSelectAll() {
    const visibleUserIds = availableUsers.slice(0, 20).map(u => u.id);
    const allSelected = visibleUserIds.every(id => selectedUsers.has(id));

    if (allSelected) {
      // Deselect all
      setSelectedUsers(prev => {
        const newSet = new Set(prev);
        visibleUserIds.forEach(id => newSet.delete(id));
        return newSet;
      });
    } else {
      // Select all
      setSelectedUsers(prev => {
        const newSet = new Set(prev);
        visibleUserIds.forEach(id => newSet.add(id));
        return newSet;
      });
    }
  }

  // Bulk add users to group (for distribution groups)
  async function handleBulkAdd() {
    const usersToAdd = allUsers.filter(u => selectedUsers.has(u.id));

    if (usersToAdd.length === 0) {
      toast.error('No users selected');
      return;
    }

    setBulkProgress({
      total: usersToAdd.length,
      completed: 0,
      currentUser: usersToAdd[0].displayName,
      results: []
    });

    const results: OperationProgress['results'] = [];

    for (let i = 0; i < usersToAdd.length; i++) {
      const user = usersToAdd[i];
      setBulkProgress(prev => prev ? {
        ...prev,
        completed: i,
        currentUser: user.displayName
      } : null);

      try {
        const result = await addUserToGroup(clientId, user.id, group.id);
        results.push({
          userId: user.id,
          userName: user.displayName,
          success: result.success,
          error: result.error
        });
      } catch (err) {
        results.push({
          userId: user.id,
          userName: user.displayName,
          success: false,
          error: err instanceof Error ? err.message : 'Unknown error'
        });
      }
    }

    // Final progress update
    setBulkProgress(prev => prev ? {
      ...prev,
      completed: usersToAdd.length,
      currentUser: '',
      results
    } : null);

    // Show summary toast
    const successCount = results.filter(r => r.success).length;
    const failCount = results.filter(r => !r.success).length;

    if (failCount === 0) {
      toast.success(`Added ${successCount} member${successCount !== 1 ? 's' : ''} to group`);
    } else if (successCount === 0) {
      toast.error(`Failed to add all ${failCount} members`);
    } else {
      toast(`Added ${successCount} of ${results.length} members`, { icon: '⚠️' });
    }

    // Clear selection and refresh members
    setSelectedUsers(new Set());
    await fetchMembers();

    // Clear progress after a short delay to show completion
    setTimeout(() => setBulkProgress(null), 2000);
  }

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-card rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold text-foreground truncate">
                {group.displayName}
              </h2>
              <p className="text-sm text-muted-foreground">
                {group.groupType} Group
              </p>
              {group.mail && (
                <div className="flex items-center gap-1 mt-1">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <a
                    href={`mailto:${group.mail}`}
                    className="text-sm text-primary-600 dark:text-primary-400 hover:underline truncate"
                  >
                    {group.mail}
                  </a>
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 text-muted-foreground hover:text-foreground rounded-lg hover:bg-accent"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Permission Error Warning */}
          {permissionError && (
            <div className="mx-4 mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                Missing GDAP Role: Groups Administrator
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                This client's GDAP relationship (likely created by Pax8) doesn't include the Groups Administrator role.
                Consents won't fix this. To manage group membership, either:
              </p>
              <ul className="text-xs text-amber-700 dark:text-amber-300 mt-1 list-disc list-inside">
                <li>Create a new GDAP relationship using Client Onboarding</li>
                <li>Manage groups directly in MS Admin Center</li>
              </ul>
            </div>
          )}

          {/* Add User Section */}
          <div className="p-4 border-b border-border">
            {/* Progress overlay for bulk operations */}
            {bulkProgress && (
              <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                    {bulkProgress.completed < bulkProgress.total
                      ? `Adding members... (${bulkProgress.completed + 1} of ${bulkProgress.total})`
                      : 'Complete!'}
                  </span>
                </div>
                {bulkProgress.currentUser && (
                  <p className="text-sm text-blue-700 dark:text-blue-300 ml-8">
                    Adding: {bulkProgress.currentUser}
                  </p>
                )}
                <div className="mt-2 w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(bulkProgress.completed / bulkProgress.total) * 100}%` }}
                  ></div>
                </div>
                {bulkProgress.completed === bulkProgress.total && bulkProgress.results.length > 0 && (
                  <div className="mt-3 text-sm">
                    <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                      <Check className="h-4 w-4" />
                      {bulkProgress.results.filter(r => r.success).length} added successfully
                    </div>
                    {bulkProgress.results.some(r => !r.success) && (
                      <div className="flex items-center gap-2 text-red-700 dark:text-red-400 mt-1">
                        <AlertTriangle className="h-4 w-4" />
                        {bulkProgress.results.filter(r => !r.success).length} failed
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {!showAddUser ? (
              <button
                onClick={() => setShowAddUser(true)}
                disabled={bulkProgress !== null}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors disabled:opacity-50"
              >
                <Plus className="h-4 w-4" />
                Add Member{isDistributionGroup ? 's' : ''}
              </button>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">
                    Add Member{isDistributionGroup ? 's' : ''}
                  </span>
                  <button
                    onClick={() => {
                      setShowAddUser(false);
                      setUserSearch('');
                      setSelectedUsers(new Set());
                    }}
                    disabled={bulkProgress !== null}
                    className="text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>

                {/* Distribution group notice */}
                {isDistributionGroup && (
                  <div className="p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded text-xs text-amber-800 dark:text-amber-200">
                    <strong>Distribution Group:</strong> Select multiple users, then click "Add Selected". This uses Exchange Online and takes ~30-60 seconds per user.
                  </div>
                )}

                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    disabled={bulkProgress !== null}
                    className="w-full pl-9 pr-4 py-2 text-sm border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
                  />
                </div>

                {loadingUsers ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
                    <span className="ml-2 text-sm text-muted-foreground">Loading users...</span>
                  </div>
                ) : availableUsers.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-2">
                    {userSearch ? 'No matching users found' : 'All users are already members'}
                  </p>
                ) : isDistributionGroup ? (
                  /* Multi-select mode for distribution groups */
                  <>
                    <div className="flex items-center justify-between text-sm mb-2">
                      <button
                        onClick={toggleSelectAll}
                        disabled={bulkProgress !== null}
                        className="text-primary-600 dark:text-primary-400 hover:underline disabled:opacity-50"
                      >
                        {availableUsers.slice(0, 20).every(u => selectedUsers.has(u.id)) ? 'Deselect All' : 'Select All'}
                      </button>
                      <span className="text-muted-foreground">
                        {selectedUsers.size} selected
                      </span>
                    </div>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {availableUsers.slice(0, 20).map(user => (
                        <label
                          key={user.id}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm rounded-lg cursor-pointer transition-colors ${
                            selectedUsers.has(user.id)
                              ? 'bg-primary-50 dark:bg-primary-900/30'
                              : 'hover:bg-accent'
                          } ${bulkProgress ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedUsers.has(user.id)}
                            onChange={() => toggleUserSelection(user.id)}
                            disabled={bulkProgress !== null}
                            className="h-4 w-4 text-primary-600 rounded border-border focus:ring-primary-500"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-foreground truncate">{user.displayName}</p>
                            <p className="text-xs text-muted-foreground truncate">{user.userPrincipalName}</p>
                          </div>
                        </label>
                      ))}
                      {availableUsers.length > 20 && (
                        <p className="text-xs text-muted-foreground text-center py-1">
                          +{availableUsers.length - 20} more (type to search)
                        </p>
                      )}
                    </div>
                    {selectedUsers.size > 0 && (
                      <button
                        onClick={handleBulkAdd}
                        disabled={bulkProgress !== null}
                        className="w-full mt-3 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {bulkProgress
                          ? 'Adding...'
                          : `Add ${selectedUsers.size} Selected Member${selectedUsers.size !== 1 ? 's' : ''}`}
                      </button>
                    )}
                  </>
                ) : (
                  /* Single-click mode for regular groups */
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {availableUsers.slice(0, 10).map(user => (
                      <button
                        key={user.id}
                        onClick={() => handleAddUser(user.id, user.displayName)}
                        disabled={addingUser === user.id}
                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm rounded-lg hover:bg-accent disabled:opacity-50"
                      >
                        {addingUser === user.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                        ) : (
                          <Plus className="h-4 w-4 text-green-500" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-foreground truncate">{user.displayName}</p>
                          <p className="text-xs text-muted-foreground truncate">{user.userPrincipalName}</p>
                        </div>
                      </button>
                    ))}
                    {availableUsers.length > 10 && (
                      <p className="text-xs text-muted-foreground text-center py-1">
                        +{availableUsers.length - 10} more (type to search)
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Content - Member List */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                <span className="ml-3 text-muted-foreground">Loading members...</span>
              </div>
            ) : error ? (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-red-700 dark:text-red-400">{error}</p>
              </div>
            ) : members.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-12 w-12 mx-auto text-muted-foreground" />
                <p className="mt-2 text-muted-foreground">No members in this group</p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm text-muted-foreground">
                    {members.length} member{members.length !== 1 ? 's' : ''}
                  </p>
                  <button
                    onClick={fetchMembers}
                    disabled={loading}
                    className="flex items-center gap-1.5 px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-accent rounded transition-colors disabled:opacity-50"
                    title="Refresh members from live API"
                  >
                    <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>
                <ul className="divide-y divide-border">
                  {members.map(member => (
                    <li key={member.id} className="py-3 flex items-center gap-3 group">
                      <div className="flex-shrink-0 h-10 w-10 bg-muted rounded-full flex items-center justify-center">
                        {getMemberIcon(member.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {member.displayName}
                        </p>
                        {member.email && (
                          <p className="text-sm text-muted-foreground truncate">
                            {member.email}
                          </p>
                        )}
                      </div>
                      <span className="text-xs px-2 py-1 bg-muted text-muted-foreground rounded">
                        {getMemberTypeLabel(member.type)}
                      </span>
                      {member.type === 'user' && (
                        <button
                          onClick={() => handleRemoveUser(member)}
                          disabled={removingUser === member.id}
                          className="opacity-0 group-hover:opacity-100 p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-all disabled:opacity-50"
                          title="Remove from group"
                        >
                          {removingUser === member.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-border space-y-3">
            {/* Delete Confirmation */}
            {showDeleteConfirm ? (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">
                  Are you sure you want to delete "{group.displayName}"?
                </p>
                <p className="text-xs text-red-700 dark:text-red-300 mb-3">
                  This action cannot be undone. All members will be removed from this group.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={deleting}
                    className="flex-1 px-3 py-1.5 text-sm bg-muted text-muted-foreground rounded-lg hover:bg-accent transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteGroup}
                    disabled={deleting}
                    className="flex-1 px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {deleting ? (
                      <>
                        <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                        Deleting...
                      </>
                    ) : (
                      'Delete Group'
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-4 py-2 text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-sm font-medium flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete Group
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2 bg-muted text-muted-foreground rounded-lg hover:bg-accent transition-colors"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
